import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import type { Organization } from '@/types/organizations';

type OrganizationWithRole = Organization & { role?: string };

export interface CreateOrganizationPayload {
  name: string;
  domain?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  subscription_plan?: Organization['subscription_plan'];
  max_users?: number;
  is_active?: boolean;
}

export const organizationsKeys = {
  all: ['organizations'] as const,
  detail: (orgId: string) => [...organizationsKeys.all, orgId] as const,
};

export const listOrganizations = () =>
  apiGet<OrganizationWithRole[]>('/organizations');

export const createOrganization = (payload: CreateOrganizationPayload) =>
  apiPost<Organization>('/organizations', payload);

export const updateOrganization = (orgId: string, payload: Partial<Organization>) =>
  apiPut<Organization>(`/organizations/${orgId}`, payload);

export const getOrganizationById = (orgId: string) =>
  apiGet<OrganizationWithRole>(`/organizations/${orgId}`);

export const checkSlugAvailability = (orgId: string, slug: string) =>
  apiGet<{ slug: string; available: boolean }>(
    `/organizations/${orgId}/slug-availability?slug=${encodeURIComponent(slug)}`,
  );

export const useOrganizationsQuery = (enabled = true) =>
  useQuery({
    queryKey: organizationsKeys.all,
    queryFn: listOrganizations,
    enabled,
  });

export const useOrganizationQuery = (orgId?: string) =>
  useQuery({
    queryKey: organizationsKeys.detail(orgId || 'none'),
    queryFn: () => getOrganizationById(orgId as string),
    enabled: !!orgId,
  });

export const useCreateOrganizationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
    },
  });
};

export const useUpdateOrganizationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, payload }: { orgId: string; payload: Partial<Organization> }) =>
      updateOrganization(orgId, payload),
    onSuccess: (updatedOrg) => {
      queryClient.setQueryData<OrganizationWithRole[]>(
        organizationsKeys.all,
        (prev) => prev?.map((org) => (org.id === updatedOrg.id ? { ...org, ...updatedOrg } : org)) ?? prev,
      );
      void queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
      void queryClient.invalidateQueries({ queryKey: organizationsKeys.detail(updatedOrg.id) });
    },
  });
};

export const useSwitchOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (organizationId: string) => {
      localStorage.setItem('current_organization_id', organizationId);
      return organizationId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
    },
  });
};
